import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import type { RootState } from "@/store/store";
import { RoutesLink } from "@/utility/RouteLinks";
import type { IAccessGuard } from "./types";

export const AccessGuard: React.FC<IAccessGuard> = ({
    element,
    accessKey,
    teamAccess,
}) => {
    const { user: userDetails, userDetailsLoading } = useSelector(
        (state: RootState) => state.user,
    );

    // Wait until user details are loaded to prevent race conditions / incorrect redirection
    if (
        !userDetails &&
        (userDetailsLoading === "pending" || userDetailsLoading === "idle")
    ) {
        return null;
    }

    // Admins bypass designation access restrictions
    const isAdmin =
        userDetails?.type === "admin" || userDetails?.type === "super_admin";

    // Level 0 or 1 gets all access (bypass designation access restrictions)
    const level = userDetails?.designation?.level;
    const isLevelAdmin =
        level !== undefined &&
        level !== null &&
        (Number(level) === 0 || Number(level) === 1);

    if (isAdmin || isLevelAdmin) {
        return <>{element}</>;
    }

    // Check specific access keys (e.g. attendance, leave, wfh)
    if (accessKey) {
        let hasAccess = false;

        // 1. Try level-based access check first (if level matches accessKey directly)
        if (level !== undefined && level !== null) {
            if (Array.isArray(level)) {
                hasAccess = level.some((l: any) => String(l) === String(accessKey));
            } else {
                hasAccess = String(level) === String(accessKey);
            }
        }

        // 2. Fallback to check from menuBar if level check did not grant access
        if (!hasAccess) {
            const menuBar = userDetails?.designation?.menuBar?.keyNames;
            hasAccess = Array.isArray(menuBar) && menuBar.includes(accessKey);
        }

        if (!hasAccess) {
            return <Navigate to={RoutesLink.dashboard} replace />;
        }
    }

    // Check team access restrictions
    if (teamAccess && !userDetails?.designation?.canAccessTeam) {
        return <Navigate to={RoutesLink.dashboard} replace />;
    }

    return <>{element}</>;
};

export default AccessGuard;
